/**
 * Build-time feature flags.
 *
 * Read at MODULE SCOPE, deliberately. `import.meta.env.X` is a literal Vite
 * substitutes at build time, so `DATA_RIGHTS_ENABLED` becomes `false` in a
 * production bundle and every `DATA_RIGHTS_ENABLED && …` branch is eliminated
 * outright. That is what makes §6.2's "test the flag-off production bundle"
 * provable the same way `noMocksInProduction.test.ts` proves it for MSW: the
 * strings are not in the file. A flag read inside a component would ship the
 * dark screens' markup and their copy to every user and merely not render them.
 *
 * `scripts/assert-build-env.mjs` refuses a production build that sets this to
 * `true`, so "shipped dark" is enforced rather than intended. Sprint 1.11
 * removes that check in the same change that makes export and deletion work.
 */
export const DATA_RIGHTS_ENABLED = import.meta.env.VITE_FEATURE_DATA_RIGHTS === 'true'

/** Sprint 1.9.15: on-request readings, Day and Compare. Off until owner release. */
export const EXPERIMENTS_ENABLED = import.meta.env.VITE_FEATURE_EXPERIMENTS === 'true'

/**
 * The synthetic headband on `/record/connect` (Sprint 1.9.7).
 *
 * A testing aid with the FakeEEGWorker's behaviour and scenario knobs, so the
 * capture flow's stale / weak / no-contact / lost phases can be exercised
 * headless. Enabled wherever the API fixtures are (the `mocked` Playwright
 * project, `npm run dev`) and nowhere else: the same key gates the dynamic
 * import of the class in `RecordCapture.tsx`, so a production bundle carries
 * neither the class nor its chunk, and `noMocksInProduction.test.ts` measures
 * that. Not a second key on purpose -- an env key absent from the build
 * environment is not statically folded, and the chunk would survive.
 */
export const SYNTHETIC_HEADBAND_ENABLED = import.meta.env.VITE_API_MOCKS === 'true'

/**
 * The paired session (Sprint 1.9.13, owner ruling R5 of 2026-10-03): eyes open
 * for 180 s, then eyes closed for 180 s, two linked recordings and the Alpha
 * reactivity card.
 *
 * Built now, on the classic; OFF for users until Sprint 1.9.12 lets the Pro
 * record in the browser and the paired session is verified on a real Pro
 * (spec §2 V2). Off means today's app exactly: no choice on Ready, no block
 * rail, no Alpha reactivity card, no pair marks in History; the phase machine
 * never leaves `plan: 'ec'` because nothing dispatches `set_plan`. Read at
 * module scope for the same reason as `DATA_RIGHTS_ENABLED`: the flag-off
 * bundle carries none of it. Unlike that flag, a production build MAY set it:
 * V2 turns it on for dev by the owner's decision, as every deploy.
 *
 * The end-of-recording tone is NOT behind this flag: RECORD-END-CUE-01 shipped
 * it for the plain recording under ruling D2 before this sprint started.
 */
export const PAIRED_SESSION_ENABLED = import.meta.env.VITE_FEATURE_PAIRED_SESSION === 'true'

/**
 * The assistant (agent-1-web): the bubble on every product screen, its drawer,
 * "Ask about this" on the measure cards, and `/assistant`.
 *
 * Read at module scope for the same reason as `DATA_RIGHTS_ENABLED`: a bundle
 * built with it false or unset carries none of the assistant's markup or copy,
 * and `assistantDark.test.ts` measures that against real builds. A production
 * build MAY set it: the owner ruled on 2026-10-11 that it ships on in dev,
 * where `deploy-web.sh` passes it. With the API's `chat_enabled` off, every chat
 * route is the plain 404 and the drawer says the assistant is taking a break.
 */
export const ASSISTANT_ENABLED = import.meta.env.VITE_FEATURE_ASSISTANT === 'true'
